import React, { useEffect, useRef } from 'react';
import { useMotion } from '../../contexts/MotionContext';

type LenisInstance = {
  raf: (time: number) => void;
  destroy: () => void;
  on: (event: string, cb: (...args: unknown[]) => void) => void;
  off: (event: string, cb: (...args: unknown[]) => void) => void;
  velocity?: number;
};

type ScrollTriggerLike = {
  update: () => void;
  refresh: () => void;
  getAll: () => { kill: () => void }[];
};

const EffectsRuntime: React.FC = () => {
  const { level } = useMotion();
  const cleanupRef = useRef<null | (() => void)>(null);

  useEffect(() => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    if (level !== 'full') {
      document.documentElement.classList.remove('motion-full');
      document.documentElement.classList.add('motion-reduced');
      return;
    }

    document.documentElement.classList.remove('motion-reduced');
    document.documentElement.classList.add('motion-full');

    let canceled = false;
    let rafId: number | null = null;
    let lenis: LenisInstance | null = null;
    let ScrollTrigger: ScrollTriggerLike | null = null;

    const init = async () => {
      const [{ default: Lenis }, gsapModule, scrollTriggerModule] = await Promise.all([
        import('lenis'),
        import('gsap'),
        import('gsap/ScrollTrigger'),
      ]);

      if (canceled) return;

      const gsap =
        (gsapModule as unknown as { gsap?: unknown }).gsap ??
        (gsapModule as unknown as { default?: unknown }).default ??
        gsapModule;

      const maybeScrollTrigger =
        (scrollTriggerModule as unknown as { ScrollTrigger?: unknown }).ScrollTrigger ??
        (scrollTriggerModule as unknown as { default?: unknown }).default ??
        null;

      ScrollTrigger =
        maybeScrollTrigger &&
        typeof (maybeScrollTrigger as ScrollTriggerLike).update === 'function' &&
        typeof (maybeScrollTrigger as ScrollTriggerLike).refresh === 'function' &&
        typeof (maybeScrollTrigger as ScrollTriggerLike).getAll === 'function'
          ? (maybeScrollTrigger as ScrollTriggerLike)
          : null;

      if (ScrollTrigger && typeof (gsap as { registerPlugin?: (p: unknown) => void }).registerPlugin === 'function') {
        (gsap as { registerPlugin: (p: unknown) => void }).registerPlugin(ScrollTrigger);
      }

      lenis = new (Lenis as unknown as new (opts: Record<string, unknown>) => LenisInstance)({
        lerp: 0.1,
        smoothWheel: true,
      });

      const onLenisScroll = () => ScrollTrigger?.update();
      lenis.on('scroll', onLenisScroll);

      let currentSkew = 0;

      const raf = (time: number) => {
        if (!lenis) return;
        lenis.raf(time);

        const velocity = typeof lenis.velocity === 'number' ? lenis.velocity : 0;
        const skewTarget = velocity * 0.1;
        currentSkew += (skewTarget - currentSkew) * 0.1;
        const clamped = Math.max(Math.min(currentSkew, 5), -5);

        document.querySelectorAll<HTMLElement>('.skew-target').forEach((el) => {
          el.style.transform = `skewY(${clamped}deg)`;
        });

        rafId = requestAnimationFrame(raf);
      };

      rafId = requestAnimationFrame(raf);
      ScrollTrigger?.refresh();

      cleanupRef.current = () => {
        canceled = true;
        if (rafId) cancelAnimationFrame(rafId);
        document.querySelectorAll<HTMLElement>('.skew-target').forEach((el) => {
          el.style.transform = '';
        });
        if (ScrollTrigger) ScrollTrigger.getAll().forEach((t) => t.kill());
        if (lenis) {
          lenis.off('scroll', onLenisScroll);
          lenis.destroy();
        }
      };
    };

    void init();

    return () => {
      canceled = true;
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [level]);

  return null;
};

export default EffectsRuntime;
